import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Popover } from '@/shared/ui';

import styles from './TranscriptorDetailsPopover.module.scss';

type TranscriptorDetailsPopoverProps = {
  className?: string;
  onArchive?: () => void;
  onDelete?: () => void;
  onReorder?: () => Promise<boolean>;
  onRestore?: () => void;
};

export const TranscriptorDetailsPopover: FC<TranscriptorDetailsPopoverProps> = (
  props,
) => {
  const { className, onArchive, onDelete, onReorder, onRestore } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const handleReorder = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsReordering(true);
    try {
      await onReorder?.();
    } finally {
      setIsReordering(false);
      setIsOpen(false);
    }
  };

  const handleArchive = (event: React.MouseEvent) => {
    event.stopPropagation();
    onArchive?.();
    setIsOpen(false);
  };

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete?.();
    setIsOpen(false);
  };

  const handleRestore = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRestore?.();
    setIsOpen(false);
  };

  if (!onArchive && !onDelete && !onReorder && !onRestore) {
    return null;
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      popoverClassName={styles.popover}
      iconClassName={styles.detailsIcon}
    >
      {onReorder && (
        <Button
          onClick={handleReorder}
          variant="tertiary"
          size="small"
          icon="rotate"
          disabled={isReordering}
          className={styles.popoverButton}
        >
          {isReordering ? (
            <FormattedMessage
              id="transcriptor.reorder.processing"
              defaultMessage="Re-ordering..."
            />
          ) : (
            <FormattedMessage
              id="transcriptor.reorder.button"
              defaultMessage="Re-order"
            />
          )}
        </Button>
      )}
      {onRestore && (
        <Button
          onClick={handleRestore}
          variant="tertiary"
          size="small"
          icon="back"
          className={styles.popoverButton}
        >
          <FormattedMessage
            id="transcriptor.menu.restore"
            defaultMessage="Restore"
          />
        </Button>
      )}
      {onArchive && (
        <Button
          onClick={handleArchive}
          variant="tertiary"
          size="small"
          icon="archive"
          danger
          className={styles.popoverButton}
        >
          <FormattedMessage id="global.archive" defaultMessage="Archive" />
        </Button>
      )}
      {onDelete && (
        <Button
          onClick={handleRemove}
          variant="tertiary"
          size="small"
          danger
          icon="delete"
          className={styles.popoverButton}
        >
          <FormattedMessage
            id="transcriptor.menu.remove"
            defaultMessage="Remove"
          />
        </Button>
      )}
    </Popover>
  );
};
